import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  IconTrash,
  IconDownload,
  IconBlock,
  IconSelected,
  ButtonSecondary,
  IconExpand,
  ButtonSize,
  Checkbox,
  SidePanel,
  ButtonPrimary,
  SidePanelType,
  IconArrowRight,
  Popup,
  ClickOutsideDetector,
  IconLoader,
  PermissionEnum,
  InputText,
  responseErrorsResolver,
  ResponseError,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  onSelectAllGroup,
  onSelectDocument,
  deleteIsSuccess,
  onDocumentMove,
} from '../../../../hoc/new-application/individual/store/slices';
import {
  onSelectAllGroup as onSelectCofAllGroup,
  onSelectDocument as onSelectCofDocument,
  deleteIsSuccess as cofDeleteIsSuccess,
  onDocumentMove as onCofDocumentMove,
} from '../../../../hoc/new-application/main-representative/store/slices';
import { useCustomersUploadMutation } from '../../../../store/documents/services';
import {
  useAddIndividualApplicationDocumentsMutation,
  useAddCofApplicationDocumentsMutation,
  useLazyGetIndividualApplicationDocumentFileQuery,
  useLazyGetCofApplicationDocumentFileQuery,
  useChangeIndividualApplicationDocumentStatusMutation,
  useChangeCOFApplicationDocumentStatusMutation,
  useChangeIndividualApplicationDocumentCategoryMutation,
  useChangeCOFApplicationDocumentCategoryMutation,
  useBatchDeleteIndividualApplicationDocumentsMutation,
  useBatchDeleteCOFApplicationDocumentsMutation,
  useRenameIndividualDocumentMutation,
  useRenameCofDocumentMutation,
} from '../../../../hoc/new-application/store/services';
import {
  ApplicationDocument,
  ApplicationDocumentStatus,
  NewApplicationStatus,
} from '../../../../hoc/new-application/types';
import DocumentRow from '../../corporate/document-row';
import CreateApplicationDocument from '../../corporate/create-document';
import { UploadResponse } from '../../../../store/documents/types';
import { RegulatedCompanyDocumentsTypes } from './types';
import { useCheckDocumentStringNameMutation } from '../../../../../../../store/service';
import StepPartBlock from '../../corporate/parts/step-part-block';

import cn from '../../new-application.module.css';

const RegulatedCompanyDocuments = ({
  documents,
  applicationId,
  applicationStatus,
  search,
  allowedDocumentsCategories,
  errors,
  clearErrors,
  renameAvailable,
  isCof,
}: RegulatedCompanyDocumentsTypes) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const dropZoneInputRef = useRef<HTMLInputElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<
    ApplicationDocumentStatus | undefined
  >();
  const [uploadedFiles, setUploadedFiles] = useState<
    { name: string; id: string }[]
  >([]);
  const [uploadedFilesErrors, setUploadedFilesError] = useState<string[]>([]);
  const [expirationDate, setExpirationDate] = useState<Date | undefined>(
    undefined,
  );
  const [renamePopupVisible, setRenamePopupVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<
    ApplicationDocument | undefined
  >(undefined);
  const [documentName, setDocumentName] = useState('');
  const [nameError, setNameError] = useState('');
  const [documentGroupError, setDocumentGroupError] = useState('');
  const [attachedDocumentsErrors, setAttachedDocumentsErrors] = useState<
    Record<string, string> | undefined
  >(undefined);
  const [attachedFiles, setAttachedFiles] = useState<string[]>([]);
  const [uploadedDocumentsErrors, setUploadedDocumentsErrors] = useState<
    { name: string; error: string }[]
  >([]);

  const {
    setError,
    formState: { errors: renameErrors },
  } = useForm();

  const [
    getIndividualApplicationDocumentFile,
    { isLoading, isSuccess, isError },
  ] = useLazyGetIndividualApplicationDocumentFileQuery();
  const [
    changeIndividualDocumentStatus,
    { isLoading: statusChangeInProgress, isSuccess: statusChangeIsSuccess },
  ] = useChangeIndividualApplicationDocumentStatusMutation();
  const [changeIndividualDocumentCategory, { isLoading: moveInProgress }] =
    useChangeIndividualApplicationDocumentCategoryMutation();
  const [deleteIndividualAppicationDocuments, { isLoading: deleteInProgress }] =
    useBatchDeleteIndividualApplicationDocumentsMutation();
  const [rename, { isLoading: renameInProgress, isSuccess: renameIsSuccess }] =
    useRenameIndividualDocumentMutation();

  const [
    getCofApplicationDocumentFile,
    {
      isLoading: cofDocumentIsLoading,
      isSuccess: cofDocumentIsSuccess,
      isError: cofDocumentError,
    },
  ] = useLazyGetCofApplicationDocumentFileQuery();
  const [
    changeCofDocumentStatus,
    {
      isLoading: cofStatusChangeInProgress,
      isSuccess: cofStatusChangeIsSuccess,
    },
  ] = useChangeCOFApplicationDocumentStatusMutation();
  const [changeCOFDocumentCategory, { isLoading: cofMoveInProgress }] =
    useChangeCOFApplicationDocumentCategoryMutation();
  const [deleteCofAppicationDocuments, { isLoading: cofDeleteInProgress }] =
    useBatchDeleteCOFApplicationDocumentsMutation();
  const [
    cofRename,
    { isLoading: cofRenameInProgress, isSuccess: cofRenameIsSuccess },
  ] = useRenameCofDocumentMutation();

  const {
    newApplications: {
      individualInfo: { selectedDocumentsCounts, deleteSuccess },
      mainRepresentative: {
        selectedDocumentsCounts: cofSelectedDocumentsCount,
      },
    },
    customersDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const onRename = async () => {
    if (documentName?.length < 3) {
      setNameError('name must be longer than or equal to 3 characters');
    } else {
      setNameError('');

      let response;

      if (isCof) {
        response = (await cofRename({
          id: applicationId as string,
          documentId: selectedDocument?.id as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      } else {
        response = (await rename({
          id: applicationId as string,
          documentId: selectedDocument?.id as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      }

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
        });
      }
    }
  };

  const changeCorporateDocumentStatusAction = (
    status: ApplicationDocumentStatus,
  ) => {
    const selectedDocuments = documents?.filter(
      (item) =>
        item.isSelected && item.status !== ApplicationDocumentStatus.EXPIRED,
    );

    if (selectedDocuments && selectedDocuments.length) {
      if (isCof) {
        changeCofDocumentStatus({
          id: applicationId as string,
          body: {
            ids: selectedDocuments?.map((item) => item.id),
            status,
          },
        });
      } else {
        changeIndividualDocumentStatus({
          id: applicationId as string,
          body: {
            ids: selectedDocuments?.map((item) => item.id),
            status,
          },
        });
      }
    }
  };

  const changeCorporateDocumentCategoryAction = async (
    documentCategoryId: string,
  ) => {
    const selectedDocuments = documents?.filter(
      (item) =>
        item.isSelected && item.documentCategoryId !== documentCategoryId,
    );
    if (selectedDocuments && selectedDocuments.length) {
      let response;

      if (isCof) {
        response = await changeCOFDocumentCategory({
          id: applicationId as string,
          body: {
            ids: selectedDocuments?.map((item) => item.id),
            documentCategoryId,
          },
        });
      } else {
        response = await changeIndividualDocumentCategory({
          id: applicationId as string,
          body: {
            ids: selectedDocuments?.map((item) => item.id),
            documentCategoryId,
          },
        });
      }
      if (response) {
        if (isCof) {
          dispatch(onCofDocumentMove(documentCategoryId));
        } else {
          dispatch(onDocumentMove(documentCategoryId));
        }
      }
    }
  };

  const onDeleteDocuments = async () => {
    const selectedDocuments = documents?.filter((item) => item.isSelected);

    if (selectedDocuments && selectedDocuments.length) {
      let response;

      if (isCof) {
        response = await deleteCofAppicationDocuments({
          id: applicationId as string,
          documentId: selectedDocuments.map((item) => item.id),
        });
      } else {
        response = await deleteIndividualAppicationDocuments({
          id: applicationId as string,
          documentId: selectedDocuments.map((item) => item.id),
        });
      }

      if (response) {
        setDeletePopupVisible(false);

        const nextDocuments = documents?.filter((document) =>
          selectedDocuments.every((item) => document.id !== item.id),
        );

        if (isCof) {
          dispatch(cofDeleteIsSuccess(nextDocuments as ApplicationDocument[]));
        } else {
          dispatch(deleteIsSuccess(nextDocuments as ApplicationDocument[]));
        }
      }
    }
  };

  const getFiles = async () => {
    const selectedDocuments = documents?.filter((item) => item.isSelected);

    if (selectedDocuments && selectedDocuments.length) {
      let downloadedFiles;

      if (isCof) {
        downloadedFiles = await Promise.all(
          selectedDocuments.map(async (item) => {
            const response = await getCofApplicationDocumentFile({
              id: applicationId as string,
              documentId: item.id,
            });

            return response.data;
          }),
        );
      } else {
        downloadedFiles = await Promise.all(
          selectedDocuments.map(async (item) => {
            const response = await getIndividualApplicationDocumentFile({
              id: applicationId as string,
              documentId: item.id,
            });

            return response.data;
          }),
        );
      }

      if (downloadedFiles) {
        downloadedFiles.forEach((file, index) => {
          const hiddenElement = document.createElement('a');
          const url = window.URL || window.webkitURL;

          // @ts-ignore
          const blob = url.createObjectURL(file);
          hiddenElement.href = blob;
          hiddenElement.target = '_blank';
          hiddenElement.download = selectedDocuments[index].name;
          hiddenElement.click();
        });
      }
    }
  };

  const onGroupSelect = (selected: boolean) => {
    if (isCof) {
      dispatch(onSelectCofAllGroup(selected));
    } else {
      dispatch(onSelectAllGroup(selected));
    }
  };

  const [checkDocumentName, { isLoading: checkDocumentNameInProgress }] =
    useCheckDocumentStringNameMutation();

  const [upload, { isLoading: fileUploadInProgress }] =
    useCustomersUploadMutation();
  const [
    addIndividualApplicationDocument,
    { isLoading: documentSubmitInProgress },
  ] = useAddIndividualApplicationDocumentsMutation();
  const [
    addCofApplicationDocument,
    { isLoading: cofDocumentSubmitInProgress },
  ] = useAddCofApplicationDocumentsMutation();

  const handleCloseCreateDocumentSidepanel = () => {
    setAttachedDocumentsErrors(undefined);
    setUploadedDocumentsErrors([]);
    setExpirationDate(undefined);

    if (uploadInputRef?.current) {
      uploadInputRef.current.value = '';
    }

    if (dropZoneInputRef?.current) {
      dropZoneInputRef.current.value = '';
    }

    setUploadedFiles([]);
    setSelectedGroup(undefined);
    setPanelIsOpen(false);
    setUploadedFilesError([]);
    setAttachedDocumentsErrors(undefined);
    setAttachedFiles([]);
  };

  const onSubmitDocuments = async () => {
    if (!selectedGroup) {
      setDocumentGroupError('Group is required');
    } else {
      setDocumentGroupError('');
      setAttachedDocumentsErrors(undefined);

      const nextAttachedDocumentsErrors = { ...attachedDocumentsErrors };

      const nextAttachedFiles = ([] as string[]).concat(attachedFiles);

      const responses = await Promise.all(
        uploadedFiles
          ?.filter((item) => nextAttachedFiles?.every((id) => item.id !== id))
          .map(async (item) => {
            const body: {
              uploadId: string;
              name: string;
              expirationDate?: string;
              documentCategoryId?: string;
            } = {
              uploadId: item.id,
              name: item.name,
            };

            if (selectedGroup) {
              body.documentCategoryId = selectedGroup;
            }

            if (expirationDate) {
              body.expirationDate = expirationDate.toISOString();
            }

            let response;

            if (isCof) {
              response = (await addCofApplicationDocument({
                id: applicationId as string,
                body,
              })) as {
                error?: {
                  data: {
                    message?: {
                      constraints: Record<string, string>;
                    }[];
                  };
                };
              };
            } else {
              response = (await addIndividualApplicationDocument({
                id: applicationId as string,
                body,
              })) as {
                error?: {
                  data: {
                    message?: {
                      constraints: Record<string, string>;
                    }[];
                  };
                };
              };
            }

            if (response.error && response?.error?.data?.message?.length) {
              const errorItem = response?.error?.data?.message[0].constraints;

              if (errorItem) {
                const [documentValue] = Object.values(errorItem);

                nextAttachedDocumentsErrors[item.id] = documentValue;
              }
            } else {
              nextAttachedFiles.push(item.id);
            }

            return response;
          }),
      );

      setAttachedFiles(nextAttachedFiles);
      setAttachedDocumentsErrors(nextAttachedDocumentsErrors);

      if (responses && responses.length) {
        if (!responses?.find((item) => item.error)) {
          handleCloseCreateDocumentSidepanel();
        }
      }
    }
  };

  const onUpload = async (files: FileList) => {
    setUploadedFilesError([]);
    setUploadedDocumentsErrors([]);

    const nextUploadedFilesErrors: string[] = [];
    const nextUploadedDocumentsErrors: { name: string; error: string }[] = [];

    const responseFiles = await Promise.all(
      Object.keys(files).map(async (file) => {
        const checkNameResponse = (await checkDocumentName({
          value: files[file as any].name,
        })) as {
          error?: {
            data: {
              message?: {
                constraints: Record<string, string>;
              }[];
            };
          };
        };

        let nextFile;

        if (!checkNameResponse?.error) {
          const response = (await upload(files[file as any])) as unknown as {
            data?: UploadResponse[];
            error?: {
              originalStatus: number;
              data?: {
                message?: string;
              };
            };
            message?: string;
          };

          if (response && response.data) {
            nextFile = { ...response.data[0] };

            let fileName = files[file as any]?.name;

            if (
              response.data[0] &&
              response.data[0]?.mimeType?.includes('image')
            ) {
              fileName = `${files[file as any]?.name.slice(0, files[file as any]?.name.lastIndexOf('.'))}.jpg`;
            }

            nextFile.name = fileName;
          } else if (response.error) {
            if (response.error.originalStatus === 413) {
              nextUploadedFilesErrors.push(
                `${files[file as any].name} size too large`,
              );
            } else {
              // eslint-disable-next-line no-lonely-if
              if (response?.error?.data?.message) {
                nextUploadedFilesErrors.push(response?.error?.data?.message);
              }
            }
          }
        } else if (
          checkNameResponse?.error &&
          checkNameResponse?.error?.data?.message
        ) {
          const errorItem =
            checkNameResponse?.error?.data?.message[0]?.constraints;

          if (errorItem) {
            nextUploadedDocumentsErrors.push({
              name: files[file as any].name,
              error: Object.values(errorItem)[0],
            });
          }
        }

        return nextFile;
      }),
    );

    setUploadedFilesError(nextUploadedFilesErrors);
    setUploadedDocumentsErrors(nextUploadedDocumentsErrors);

    if (responseFiles && responseFiles.length) {
      const nextUploadedFiles = ([] as { name: string; id: string }[]).concat(
        uploadedFiles,
      );

      responseFiles.forEach((item) => {
        if (item && item.name && item.id) {
          nextUploadedFiles.push({
            name: item?.name,
            id: item?.id,
          });
        }
      });

      setUploadedFiles(nextUploadedFiles);
    }

    if (uploadInputRef?.current) {
      uploadInputRef.current.value = '';
    }
    if (dropZoneInputRef?.current) {
      dropZoneInputRef.current.value = '';
    }
  };

  const onDelete = (id: string) => {
    if (uploadInputRef?.current) {
      uploadInputRef.current.value = '';
    }

    if (dropZoneInputRef?.current) {
      dropZoneInputRef.current.value = '';
    }

    setUploadedFiles(uploadedFiles.filter((item) => item.id !== id));

    const nextAttachedDocumentsErrors = { ...attachedDocumentsErrors };

    if (nextAttachedDocumentsErrors[id]) {
      delete nextAttachedDocumentsErrors[id];

      setAttachedDocumentsErrors(nextAttachedDocumentsErrors);
    }
  };

  useEffect(() => {
    if (renameIsSuccess || cofRenameIsSuccess) {
      setRenamePopupVisible(false);
      setDocumentName('');
      setSelectedDocument(undefined);
      setNameError('');
    }
  }, [renameIsSuccess, cofRenameIsSuccess]);

  useEffect(() => {
    if (deleteSuccess) {
      setDeletePopupVisible(false);
    }
  }, [deleteSuccess]);

  useEffect(() => {
    if (
      isSuccess ||
      isError ||
      statusChangeIsSuccess ||
      cofDocumentIsSuccess ||
      cofDocumentError ||
      cofStatusChangeIsSuccess
    ) {
      if (isCof) {
        dispatch(onSelectCofAllGroup(false));
      } else {
        dispatch(onSelectAllGroup(false));
      }

      if (statusChangeIsSuccess || cofStatusChangeIsSuccess) {
        setSelectedStatus(undefined);
      }
    }
  }, [
    isSuccess,
    isError,
    statusChangeIsSuccess,
    cofDocumentIsSuccess,
    cofDocumentError,
    cofStatusChangeIsSuccess,
    isCof,
  ]);

  useEffect(() => {
    if (selectedDocument && !documentName) {
      setDocumentName(selectedDocument.name);
    }
  }, [selectedDocument]);

  useEffect(() => {
    if (documentGroupError && selectedGroup) {
      setDocumentGroupError('');
    }
  }, [documentGroupError, selectedGroup]);

  const createButtonVisible =
    applicationStatus !== NewApplicationStatus.ACCEPTED &&
    applicationStatus !== NewApplicationStatus.DECLINED &&
    activePermissions?.includes(
      PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_CREATE_ACTION,
    );

  return (
    <StepPartBlock title="Company documents" zIndex={10}>
      <div className={cn.documentsBlockHeader}>
        {createButtonVisible && (
          <ButtonSecondary
            text="Add document"
            icon={<IconExpand />}
            className={cn.addDocumentButton}
            size={ButtonSize.SMALL}
            onClick={() => setPanelIsOpen(true)}
            tabIndex={-1}
          />
        )}

        {(selectedDocumentsCounts > 0 || cofSelectedDocumentsCount > 0) && (
          <>
            {applicationStatus !== NewApplicationStatus.ACCEPTED &&
              applicationStatus !== NewApplicationStatus.DECLINED && (
                <div className={cn.documentsControlSeparator} />
              )}
            <div className={cn.selectedDocumentsMoveWrapper}>
              {applicationStatus !== NewApplicationStatus.ACCEPTED &&
                applicationStatus !== NewApplicationStatus.DECLINED && (
                  <>
                    <ClickOutsideDetector
                      setOptionsListVisible={setMenuVisible}
                    >
                      <>
                        <ButtonSecondary
                          className={`button-white ${cn.controlButton} ${
                            menuVisible ? cn.controlButtonActive : ''
                          }`}
                          icon={<IconArrowRight />}
                          size={ButtonSize.SMALL}
                          text={
                            moveInProgress || cofMoveInProgress
                              ? 'Loading...'
                              : 'Move to group'
                          }
                          onClick={() => setMenuVisible(!menuVisible)}
                          disabled={
                            isCof
                              ? cofSelectedDocumentsCount === 0
                              : selectedDocumentsCounts === 0
                          }
                          tabIndex={-1}
                        />
                        {menuVisible && (
                          <ul className={cn.selectedDocumentsMenu}>
                            <li className={cn.selectedDocumentsMenuTitle}>
                              {`Which group to move ${isCof ? cofSelectedDocumentsCount : selectedDocumentsCounts} documents to?`}
                            </li>

                            {allowedDocumentsCategories
                              ?.filter(
                                (item) => !item.id.includes('identification'),
                              )
                              ?.map((item) => (
                                <li key={item.id}>
                                  <button
                                    type="button"
                                    className={cn.selectedDocumentsMenuButton}
                                    onClick={() => {
                                      changeCorporateDocumentCategoryAction(
                                        item.id,
                                      );
                                      setMenuVisible(false);
                                    }}
                                    tabIndex={-1}
                                  >
                                    {item.name}
                                    <div>
                                      Move <IconArrowRight />
                                    </div>
                                  </button>
                                </li>
                              ))}
                          </ul>
                        )}
                      </>
                    </ClickOutsideDetector>
                    {activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
                    ) && (
                      <>
                        <ButtonSecondary
                          className={`button-white ${cn.controlButton}`}
                          text={
                            (statusChangeInProgress ||
                              cofStatusChangeInProgress) &&
                            selectedStatus ===
                              ApplicationDocumentStatus.ACCEPTED
                              ? 'Loading...'
                              : 'Accept'
                          }
                          tabIndex={-1}
                          icon={
                            (statusChangeInProgress ||
                              cofStatusChangeInProgress) &&
                            selectedStatus ===
                              ApplicationDocumentStatus.ACCEPTED ? (
                              <IconLoader />
                            ) : (
                              <IconSelected />
                            )
                          }
                          size={ButtonSize.SMALL}
                          onClick={() => {
                            setSelectedStatus(
                              ApplicationDocumentStatus.ACCEPTED,
                            );

                            changeCorporateDocumentStatusAction(
                              ApplicationDocumentStatus.ACCEPTED,
                            );
                          }}
                          disabled={
                            statusChangeInProgress || cofStatusChangeInProgress
                          }
                        />
                        <ButtonSecondary
                          className={`button-white ${cn.controlButton}`}
                          text={
                            (statusChangeInProgress ||
                              cofStatusChangeInProgress) &&
                            selectedStatus ===
                              ApplicationDocumentStatus.REJECTED
                              ? 'Loading...'
                              : 'Reject'
                          }
                          icon={
                            (statusChangeInProgress ||
                              cofStatusChangeInProgress) &&
                            selectedStatus ===
                              ApplicationDocumentStatus.REJECTED ? (
                              <IconLoader />
                            ) : (
                              <IconBlock />
                            )
                          }
                          tabIndex={-1}
                          onClick={() => {
                            setSelectedStatus(
                              ApplicationDocumentStatus.REJECTED,
                            );

                            changeCorporateDocumentStatusAction(
                              ApplicationDocumentStatus.REJECTED,
                            );
                          }}
                          size={ButtonSize.SMALL}
                          disabled={
                            statusChangeInProgress || cofStatusChangeInProgress
                          }
                        />
                      </>
                    )}
                  </>
                )}
              <ButtonSecondary
                className={`button-white ${cn.controlButton}`}
                text="Downloads"
                icon={
                  isLoading || cofDocumentIsLoading ? (
                    <IconLoader />
                  ) : (
                    <IconDownload />
                  )
                }
                size={ButtonSize.SMALL}
                onClick={getFiles}
                disabled={isLoading || cofDocumentIsLoading}
                tabIndex={-1}
              />
              {applicationStatus !== NewApplicationStatus.ACCEPTED &&
                applicationStatus !== NewApplicationStatus.DECLINED &&
                activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
                ) && (
                  <ButtonSecondary
                    className={`button-white ${cn.controlButton}`}
                    text="Delete"
                    icon={<IconTrash />}
                    size={ButtonSize.SMALL}
                    onClick={() => setDeletePopupVisible(true)}
                    tabIndex={-1}
                  />
                )}
            </div>
          </>
        )}
      </div>
      <div className={cn.documentsHeader}>
        <div className={cn.documentsHeaderRow}>
          <Checkbox
            name="select-all"
            type="checkbox"
            title=""
            checked={
              isCof
                ? Boolean(
                    cofSelectedDocumentsCount > 0 &&
                    cofSelectedDocumentsCount === documents?.length,
                  )
                : Boolean(
                    selectedDocumentsCounts > 0 &&
                    selectedDocumentsCounts === documents?.length,
                  )
            }
            disabled={!documents?.length}
            onChange={(e) => onGroupSelect(e.target.checked)}
            tabIndex={-1}
          />
          Name
        </div>
        <div className={cn.documentsHeaderRow}>Status</div>
      </div>
      {allowedDocumentsCategories?.map((category) => (
        <DocumentRow
          key={category.id}
          title={category?.name}
          documents={documents?.filter(
            (item) =>
              item.documentCategoryId === category.id ||
              item.category === category?.category,
          )}
          onSelect={(selected, id) => {
            if (isCof) {
              dispatch(onSelectCofDocument({ selected, id }));
            } else {
              dispatch(onSelectDocument({ selected, id }));
            }
          }}
          error={
            ((errors &&
              errors[category.id] &&
              errors[category.id]?.message) as string) ||
            (errors &&
              errors[category?.category as string] &&
              (errors[category?.category as string]?.message as string)) ||
            ''
          }
          categoryId={category.id}
          categoryName={category?.category}
          applicationId={applicationId}
          applicationStatus={applicationStatus}
          clearErrors={clearErrors}
          search={search}
          setRenamePopupVisible={setRenamePopupVisible}
          setSelectedDocument={setSelectedDocument}
          renameAvailable={renameAvailable}
          isIndividual={!isCof}
          isCof={isCof}
        />
      ))}
      <Popup
        title={`Delete ${isCof ? cofSelectedDocumentsCount : selectedDocumentsCounts} documents?`}
        onCancelClick={() => setDeletePopupVisible(false)}
        onOkClick={onDeleteDocuments}
        cancelButtonText="Cancel"
        okButtonText={
          deleteInProgress || cofDeleteInProgress ? 'Loading...' : 'Delete'
        }
        isVisible={deletePopupVisible}
      />
      <Popup
        title="Rename"
        onCancelClick={() => {
          setRenamePopupVisible(false);
          setSelectedDocument(undefined);
          setDocumentName('');
          setNameError('');
        }}
        onOkClick={onRename}
        cancelButtonText="Cancel"
        okButtonText={
          renameInProgress || cofRenameInProgress ? 'Loading...' : 'Rename'
        }
        isVisible={renamePopupVisible}
        okButtonDisabled={selectedDocument?.name === documentName}
      >
        <InputText
          name="documentName"
          value={documentName}
          onChange={(e) => setDocumentName(e.target.value)}
          placeholder="Enter document name"
          labelClassName={cn.renameInput}
          error={nameError || (renameErrors?.name?.message as string)}
        />
      </Popup>
      <SidePanel
        type={SidePanelType.Customers}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleCloseCreateDocumentSidepanel}
        title="Add new document"
        sidepanelExpandedKey="application-document"
        footer={
          <>
            <ButtonPrimary
              text={
                documentSubmitInProgress || cofDocumentSubmitInProgress
                  ? 'Loading...'
                  : 'Add document'
              }
              onClick={onSubmitDocuments}
              className={cn.documentSubmit}
              bold
              tabIndex={-1}
            />
            <ButtonSecondary
              text="Cancel"
              onClick={() => setPanelIsOpen(true)}
              bold
              tabIndex={-1}
            />
          </>
        }
      >
        <CreateApplicationDocument
          uploadInputRef={uploadInputRef}
          dropZoneInputRef={dropZoneInputRef}
          documents={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          uploadedFilesErrors={uploadedFilesErrors}
          selectedGroup={selectedGroup}
          onUpload={onUpload}
          onSelect={setSelectedGroup}
          fileUploadInProgress={
            checkDocumentNameInProgress || fileUploadInProgress
          }
          onDelete={onDelete}
          setExpirationDate={setExpirationDate}
          expirationDate={expirationDate}
          allowedDocumentsCategories={allowedDocumentsCategories}
          documentGroupError={documentGroupError}
          attachedDocumentsErrors={attachedDocumentsErrors}
          attachedFiles={attachedFiles}
          uploadedDocumentsErrors={uploadedDocumentsErrors}
        />
      </SidePanel>
    </StepPartBlock>
  );
};

export default RegulatedCompanyDocuments;
